// Modules
import React from "react";
// @ts-ignore
import { LocalizationContext } from "react-localize";
import moment from "moment";
import ReservationService from 'Services/reservation.service';
// @ts-ignore
import sessionStorage from "store/storages/sessionStorage";
// Styles
import "./BookingInfo.sass";

// Interfaces
import {  ReservationDetails, ReservationViewFields, showReservationField, SiteType } from "Interfaces/AYPResultInterface";
import {RoomModal} from "Interfaces/RoomModalInterface";
import { Tip } from "Interfaces/TipInterface";

// Utils
import {numberWithCommas} from "Utils";

// Constants
import {InitRoomModal} from "Constants/InitInterfaces";

// Components
import {MvRoomModal} from "Generic/MvModal";
import {MvResortModal} from "Generic/MvModal";
import { ReservationType } from "Interfaces/GetReservations";
import { RequestStatus, ScreenType } from "Constants";
import {ReactAppMode} from "../../../../Interfaces/ReactAppModes";
import {EXEC_MODE} from "../../../../cfg";
import ToolTip from "Generic/ToolTip";
import { Position } from "Generic/ToolTip/ToolTip";
import MvLink from "Generic/MvLink";
import CancelUnavaliableModal from "Components/Pages/CancelReservation/CancelUnavailableModal/CancelUnavailableModal";
import { trackAnalyticData } from "Utils/analytics";

type Props = {
  reservationDetails: ReservationDetails;
  pointsRequired: number;
  onEditSearchClick(): void;
  readOnly: boolean;
  comingFrom?: string;
  visitResortNewTab?: boolean;
  resType?: ReservationType;
  resId?: number;
  canCancel?: Boolean;
  setScreenType(screenType: ScreenType): void;
  reservationData?: any;
};

type State = {
  roomModal: RoomModal;
  resortModal: {
      show: boolean;
      propertyCode: string;
  };
  singleRoomInfo: any;
  roomsRequestStatus: RequestStatus;
  windowWidth: number;
  showUnavailableModal: boolean;
  cancelMessage: string
};

const InitState: State = {
  roomModal: Object.assign(InitRoomModal, {}),
  resortModal: {
    show: false,
    propertyCode: '',
  },
  singleRoomInfo: {}, 
  roomsRequestStatus: RequestStatus.NOT_STARTED,
  windowWidth: 0,
  showUnavailableModal: false,
  cancelMessage: ''
};

class BookingInfo extends React.Component<Props, State> {

  pointsTip: [Tip] = [
    {
      label: "",
      desc: "static.content.tip.booking-info-points",
    },
  ];

  reservationService: ReservationService;

  constructor(props: Props) {
    super(props);
    this.reservationService = new ReservationService();
    this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
    this.state = Object.assign({}, InitState);

  }

  checkAndExecuteRoomCall = (props: Props) => {
    const roomPoolCode = props.reservationDetails.roomPoolCode;

    if (roomPoolCode && roomPoolCode !== null && this.state.roomsRequestStatus === RequestStatus.NOT_STARTED) {
      const segments = JSON.parse(sessionStorage.read(ReservationService.SEGMENTS));
      const { resortId, roomPoolCode } = props.reservationDetails;
  
      this.fetchFloorPlan({
        segments: segments || ["PTS", "INA", "AP2"],
        resorts : [resortId],
        codeRoomPoolCode: [roomPoolCode]
      });
    }
  };

  componentDidUpdate(nextProps: Props) {
    this.checkAndExecuteRoomCall(nextProps);
  }

  componentDidMount() {
    this.updateWindowDimensions();
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentWillUnmount(): void {
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  /**
   * toggle room modal
   */
  toggleRoomModal = (): void => {
    this.setState({
      roomModal: {
        ...this.state.roomModal,
        show: !this.state.roomModal.show
      },
    } as any);
  }

  toggleResortModal(propertyCode: string) {
    this.setState({
        resortModal: {
            propertyCode,
            show: !this.state.resortModal.show,
        },
    });
  }

  /**
   * if accessibility preferences have been selected,
   * return the accessibility JSX
   */
  accessibilityPrefs() {
    const { localize } = this.context;
    const accessibilityPrefs = this.props.reservationDetails.accessibility;

    if((this.props.comingFrom === 'viewReservation' || this.props.comingFrom === 'cancelReservation') && (!accessibilityPrefs || accessibilityPrefs && accessibilityPrefs.length === 0)){
      return null;
    } 
    return (
      <div className="accessibility-prefs">
        <span className="booking-info-label">
          {localize("static.content.ayp-accessibility-prefs")}{":"}
        </span>
        {(accessibilityPrefs && accessibilityPrefs.length > 0) ? accessibilityPrefs.map((pref, idx) => {
          const comma = idx + 1 < accessibilityPrefs.length ?
            ", " : "";

          return (
            <span key={pref.value}>
              {pref.name}
              {comma}
            </span>
          );
        // }) : localize("static.content.ayp-none")}
        }) : localize("None")}
      </div>
    );
  }

  fetchFloorPlan = (roomParams:any) => {
    this.reservationService.searchRoomInfo(roomParams).then(res => {
        this.setState({
            singleRoomInfo: (res as any)[0],
            roomsRequestStatus: RequestStatus.LOADED,
        });
    }).catch(err => {
      this.setState({
        roomsRequestStatus: RequestStatus.ERRORED,
      });
    });
  }

  /**
   * if hearing accessible has been selected,
   * return the hearing accessible JSX
   */
  hearingAccessible() {
    const { localize } = this.context;
    const hearingAccessible = this.props.reservationDetails.hearingaccessible;
    if((this.props.comingFrom === 'viewReservation' || this.props.comingFrom === 'cancelReservation') && !hearingAccessible){
      return null;
    }
    return (
      <div className="hearing-accessible">
        <span className="booking-info-label">
          {localize("static.content.hearing-accessible")}{":"}
        </span>
        <span>
          {/* {hearingAccessible === true ? localize("static.content.ayp-yes") : localize("static.content.ayp-no")} */}
          {hearingAccessible === true ? localize("static.content.ayp-yes") : localize("None")}
        </span>
      </div>
    );
  }

  /**
   * return the arrival info JSX
   *
   * section includes: check-in, check-out, nights, guests, and accessibility
   * preferences
   */
  arrivalInfo(classNames: string[]) {
    const { localize } = this.context;

    const {
      checkInDate,
      checkOutDate,
      numOfNights,
      numOfGuests,
      accessibility,
    } = this.props.reservationDetails;

    let showCheckIn = true;
    let showCheckOut = true;
    let showNumNights = true;
    let showNumGuests = true;
    if (this.props.comingFrom === "viewReservation") {
      showCheckIn = this.showResField(ReservationViewFields.CHECK_IN);
      showCheckOut = this.showResField(ReservationViewFields.CHECK_OUT);
      showNumNights = this.showResField(ReservationViewFields.NUMBER_NIGHTS);
      showNumGuests = this.showResField(ReservationViewFields.NUMBER_GUESTS);
    }

    return (
      <div className={`arrival-info ${classNames.join(" ")}`}>
        <div>
          <div className="checkin-nights-guests">
            {showCheckIn && <div className="check-in">
              <span className="booking-info-label">
                {localize("static.content.check-in")}{":"}
              </span>
              <span>
                {moment(checkInDate).format('DD MMM YYYY')}
              </span>
            </div>
            }
            {showCheckOut && <div className="check-out">
              <span className="booking-info-label">
                {localize("static.content.check-out")}{":"}
              </span>
              <span>
                {moment(checkOutDate).format('DD MMM YYYY')}
              </span>
            </div>
            }
          {this.props.comingFrom !== "viewReservation" && showNumNights && <div className="nights">
              <span className="booking-info-label">
                {localize("static.content.num-of-nights")}{":"}
              </span>
              <span>
                {numOfNights}{" "}
                {numOfNights  === 1 ? localize("static.content.night") : localize("static.content.nights")}
              </span>
            </div>}
          {this.props.comingFrom !== "viewReservation" && showNumGuests && <div className="guests">
              <span className="booking-info-label">
                {localize("static.content.num-of-guests")}{":"}
              </span>
              <span>
                {numOfGuests}{" "}
                {numOfGuests  === 1 ? localize("static.content.guest") : localize("static.content.guests")}
              </span>
            </div>}
          </div>
          <div className="accessibility">
          {this.props.comingFrom === "viewReservation" && <div className="nights-guests">
              {showNumNights && <div className="nights">
                <span className="booking-info-label">
                  {localize("static.content.num-of-nights")}{":"}
                </span>
                <span>
                  {numOfNights}{" "}
                  {localize("static.content.nights")}
                </span>
              </div>
              }
              {showNumGuests && <div className="guests">
                <span className="booking-info-label">
                  {localize("static.content.num-of-guests")}{":"}
                </span>
                <span>
                  {numOfGuests}{" "}
                  {localize("static.content.guests")}
                </span>
              </div>
              }
            </div> }
            {this.accessibilityPrefs()}
            {this.hearingAccessible()}
          </div>
        </div>
        <div>
        {this.props.resType == ReservationType.UPCOMING && <MvLink label={localize("static.content.cancel-reservation-cta-label")}onClick={() => this.handleCancelMessage()} />}
        </div>
      </div>
    );
  }

  handleCancelMessage = () => {
    /** Tracking for adobe Analytics */
    const { localize } = this.context;
    trackAnalyticData('cancelTripDetail');
    /**Adding the below piece of code in order to handle manual marsha cancellation and since
     * we do not have redux we have to do an other api call to check canCancel = true
     */
    this.reservationService.getReservation(this.props.resId || 0, this.props.resType || '').then(reservation => {
    localStorage.setItem('cancel_reservation_data', JSON.stringify(reservation)); 
    var response  = reservation;
    if (response.canCancel) {
      this.props.setScreenType(ScreenType.CANCEL_RESERVATION);
    } else {
        let cancelMessage = ''
        switch (response.ruleName) {
            case 'TSW_INVALID_RESERVATION_TYPE': 
            cancelMessage = localize("static.content.cancel-tsw-invalid-reservation-type");
          break;
            case 'TSW_MARSHA_DATA_MISMATCH': 
            cancelMessage = localize("static.content.cancel-tsw-marsha-data-mismatch");
          break;
          case 'TSW_NO_CANCEL_PREVIEW_RESPONSE': 
            cancelMessage = localize("static.content.cancel-tsw-no-cancel-preview-response");
            break;
          default:
            cancelMessage = response.ruleName || '';
            break;
        }
        this.setState({ showUnavailableModal: true, cancelMessage })
    }
      }).catch(error => {
        this.setState({ showUnavailableModal: true, cancelMessage: localize("static.content.cancel-api-failure") })
      });
  }

  toggleshowUnavailableModal = () => {
    this.setState({ showUnavailableModal: !this.state.showUnavailableModal })
  }

  showResField(resViewField:ReservationViewFields) {
    return showReservationField(resViewField, this.props.reservationDetails);
  }

  updateWindowDimensions() {
      this.setState({ windowWidth: window.innerWidth });
  }

  render() {
    const { localize } = this.context;

    const {
      pointsRequired,
      reservationDetails,
      onEditSearchClick,
      readOnly,
      resType,
    } = this.props;

    const {
      resortName,
      resortLocation,
      resortImage,
      roomType,
      resortId,
      managementCompany
    } = reservationDetails;

    let destinationClick = () => {};
    let showDestinationLink = false;
    let siteType = managementCompany ? managementCompany.siteType : "";
    const imageUrl = resortImage || (managementCompany ? managementCompany.imageUrl : "")

    // temp
    if (!resType) {
      siteType = SiteType.MVC;
    }

    if (resType !== ReservationType.CANCELED) {
      switch (siteType) {
        case SiteType.MVC:
          destinationClick = () => this.toggleResortModal(resortId as string);
          showDestinationLink = true;
          break;
        case SiteType.EXPLORER:
          if([ReactAppMode.VCAP, ReactAppMode.LOCAL].includes(EXEC_MODE)) {
            destinationClick = () => window.location.href = "../explorerCollection.jsp";
            showDestinationLink = true;
          }
          break;
        case SiteType.EXCHANGE:
          if([ReactAppMode.VCAP, ReactAppMode.LOCAL].includes(EXEC_MODE)) {
            destinationClick = () => window.location.href = "../mvcAsiaPacific.jsp";
            showDestinationLink = true;
          }
          break;
      }
    }
    
    let destinationImage = imageUrl;
    if (siteType !== SiteType.MVC) {
      destinationImage = managementCompany && managementCompany.imageUrl ? managementCompany.imageUrl : "";
    }

    return (
      <div className="booking-info">
        <div className="resort-container">
          <CancelUnavaliableModal
            openModal={this.state.showUnavailableModal}
            toggleModal={this.toggleshowUnavailableModal}
            message={this.state.cancelMessage}
          />
          <MvRoomModal
            fetchLoaded={true}
            toggle={() => this.toggleRoomModal()}
            data={{...this.state.roomModal, ...this.state.singleRoomInfo}}
          />
          <MvResortModal
            show={this.state.resortModal.show}
            propertyCode={this.state.resortModal.propertyCode}
            toggle={() => this.setState({ resortModal: { ...this.state.resortModal, show: !this.state.resortModal.show } })}
            visitResortNewTab={this.props.visitResortNewTab}
          />
          <div
            className={showDestinationLink ? "resort-image resort-image-link" : "resort-image"}
            onClick={destinationClick}
          >
            <img
              src={imageUrl}
              alt={resortName}
            />
          </div>

          <div className="resort-info">
            <div className="name-address-room">
              <div className="name-address">
                <div
                  className={showDestinationLink ? "booking-info-label resort-name  mv-print-link" : "booking-info-label"}
                  onClick={destinationClick}
                >
                  {resortName}
                </div>
                <div className="resort-address">
                  {resortLocation !== "" ? resortLocation : ""}
                </div>
              </div>
              <div className="room-type-container">
                <div className="booking-info-label">
                  {localize("static.content.room-type")}{":"}
                </div>
                <br />
                <span
                  className={resType !== ReservationType.CANCELED && siteType === SiteType.MVC ? "room-type mv-print-link" : ""}
                  onClick={() => resType !== ReservationType.CANCELED && siteType === SiteType.MVC ? this.toggleRoomModal() : {}}
                  dangerouslySetInnerHTML={{ __html: roomType.description }}
                >
                </span>
              </div>
            </div>
            {this.arrivalInfo(["hide-mobile"])}
          </div>
          {this.arrivalInfo(["hide-desktop"])}
        </div>
        <div className="points-needed">
          <div className="label-and-points">
            <div className="booking-info-label">
              {this.props.comingFrom === 'cancelReservation' && localize("static.content.cancel-points-needed") }
               {this.props.comingFrom !== 'cancelReservation' && ((resType === ReservationType.CANCELED) ? 
                localize("static.content.ayp-points-prev-needed")
                :
                localize("static.content.ayp-points-needed"))
              }
              {/* TODO: commenting out tool tip - will implement at a future date */}
              {/* {(resType === ReservationType.PAST || resType === ReservationType.CANCELED) &&
                <div className='points-tooltip'>
                  <ToolTip
                    useRefToPosition={false}
                    tip={this.pointsTip}
                    tabIndex={0}
                    id='booking-info-points-tooltip'
                    position={this.state.windowWidth <=900 ? Position.RIGHT : Position.LEFT}
                  >
                    <span className='question-icon'>{"?"}</span>
                  </ToolTip>
                </div>
              } */}
            </div>
            <div className="points-container">
              {(!resType || resType === ReservationType.UPCOMING || ((resType === ReservationType.PAST || resType === ReservationType.CANCELED) && pointsRequired > 0)) &&
                <div>{numberWithCommas(pointsRequired)}</div>
              }
            </div>
          </div>

          {!readOnly &&
            <div className="edit-search-btn-container">
                <button
                  className="edit-search-btn"
                  onClick={onEditSearchClick}
                >
                  {localize("static.content.ayp-edit-search")}
                </button>
            </div>
          }
        </div>

      </div>
    );
  }
}

BookingInfo.contextType = LocalizationContext;
export default BookingInfo;

